import { LLMChoices } from "@/types";
import React, { useEffect, useRef, useState } from "react";
import { LuArrowDown } from "react-icons/lu";

import TextareaAutosize from "react-textarea-autosize";
import { Button } from "@/components/ui/button";
import { uploadFilesAsync } from "@/api/globalApiService";
import { useParams, useSearchParams } from "react-router-dom";
import styles from "./index.module.css";
import { FeatureCardProps, InsightChatCarousel } from "./components/InsightChatCarousel";
import InsightChatHistoryMenu from "./components/InsightChatHistoryMenu";
import { RxReload } from "react-icons/rx";
import { cn } from "@/lib/utils";
import MessageCard from "./components/MessageCard";
import { Account, CreativeReportMetadata } from "@/types";
import {
  fetchCreativeChatList,
  fetchCreativeChatHistory,
} from "@/screens/creatives/apiService";
import {
  CreativeReport,
  CreativeReportSection,
  ReportType,
} from "@/screens/creatives/types";
import SectionSelectorDropdown from "./components/dropdown/SectionSelectorDropdown";
import {
  FileAttachmentHandler,
  FileAttachmentsPreview,
} from "@/components/hoc/fileAttachment";
import { UploadingFile } from "@/components/hoc/fileAttachment/types";
import ModelSelectorDropdown from "./components/dropdown/ModelSelectorDropdown";
import ItemLoader from "@/components/common/loaders/ItemLoader";
import {
  Agent,
  ChatData,
  ChatSource,
  Files,
  MessageFeedback,
  MessageType,
} from "./types";
import { StreamState } from "@/services/backgroundStreamManager";
import { SqButton } from "@/components/squircle-ui/button";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";
import CreativeAttachmentCard from "./components/CreativeAttachmentCard";
import {
  RiCloseFill,
  RiContractLeftRightLine,
  RiEditLine,
  RiExpandLeftRightLine,
  RiPushpinFill,
  RiPushpinLine,
  RiSendPlane2Line,
} from "@remixicon/react";
import { useAutoScroll } from "@/hooks/useAutoScroll";
import { useBackgroundStream } from "@/hooks/useBackgroundStream";
import { useAttachedCreativesStore } from "@/screens/creatives/store/attachedCreativesStore";

export interface InsightChatRef {
  clearChat: () => void;
  getChatHistoryById: (id: string) => void;
  updateURLChatId: (id: string) => void;
  setChatError: (error: string | null) => void;
}

interface InsightChatProps {
  open: boolean;
  selectedAccount?: Account;
  loading?: boolean;
  onChatOpenChange?: (val: boolean) => void;
  reportId?: string | undefined;
  mode?: "report" | "create";
  showSectionDropdown?: boolean;
  slides?: Array<Array<FeatureCardProps>>;
  showChatHistory?: boolean;
  onChatHistoryUpdate?: (history: ChatData[]) => void;
  onActiveChatIdChange?: (id: string) => void;
  onMessagesChange?: (messages: MessageType[]) => void;
  creativeMetaData?: CreativeReportMetadata | null;
  reportConfig?: CreativeReport | null;
  selectedSection?: (CreativeReportSection & { overrideScroll?: boolean }) | null;
}

const COPILOT_ALLOWED_FILE_TYPES = ["image/*", "video/*"];

export default React.forwardRef<InsightChatRef, InsightChatProps>(function InsightChat(
  {
    open,
    selectedAccount,
    loading,
    onChatOpenChange,
    reportId,
    mode = "report",
    showSectionDropdown = true,
    slides,
    showChatHistory = true,
    onChatHistoryUpdate,
    onActiveChatIdChange,
    onMessagesChange,
    creativeMetaData,
    reportConfig,
    selectedSection,
  }: InsightChatProps,
  ref,
) {
  const { accountId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [chatId, setChatId] = useState<string | null>(searchParams.get("chatId") || null);
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [input, setInput] = useState<string>("");
  const [selectedSource, setSelectedSource] = useState<ChatSource | null>(null);
  const [typingIndicatorLoading, setTypingIndicatorLoading] = useState<boolean>(false);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const inputAreaRef = useRef<HTMLTextAreaElement>(null);
  const [llmModel, setLlmModel] = useState<LLMChoices>(LLMChoices.quality);
  const [chatHistory, setChatHistory] = useState<ChatData[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);

  const { atBottom, scrollToBottom, resetAutoScroll } =
    useAutoScroll(messageContainerRef);
  // Background stream management
  const { startStream, subscribeToExistingStream, cleanupAllListeners } =
    useBackgroundStream({
      accountId: accountId || "",
      reportId: reportId || "",
      chatId: chatId || "",
      onStreamStart: () => {
        void updateChatHistory();
      },
      onStreamUpdate: (chatIdStream: string | undefined) => {
        if (chatIdStream) {
          setChatId(chatIdStream);
          currentChatIdRef.current = chatIdStream;
        }
      },
      onStreamComplete: () => {
        setTypingIndicatorLoading(false);
        setIsStreaming(false);
      },
      onStreamError: (error: string) => {
        setChatError(error);
        setTypingIndicatorLoading(false);
        setIsStreaming(false);
      },
    });

  const [chatError, setChatError] = useState<string | null>(null);
  const [files, setFiles] = useState<Files[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isPinned, setIsPinned] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const streamListenerRef = useRef<((stream: StreamState) => void) | null>(null);
  const currentChatIdRef = useRef<string | null>(null);

  // Attached creatives from store
  const {
    attachedCreatives,
    removeAttachedCreative,
    clearAttachedCreatives, // eslint-disable-line @typescript-eslint/no-unused-vars
    shouldPinOnOpen,
    setShouldPinOnOpen,
    currentSectionMetrics,
  } = useAttachedCreativesStore();

  const updateURLChatId = (chatId: string | null) => {
    if (chatId)
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("chatId", chatId);
        return params;
      });
    else
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.delete("chatId");
        return params;
      });
  };
  const clearChat = () => {
    abortControllerRef?.current?.abort();
    setChatError(null);
    setMessages([]);
    setChatId(null);
    currentChatIdRef.current = null;
    setIsStreaming(false);
    setTypingIndicatorLoading(false);
    updateURLChatId(null);
    setSelectedSource(null);
    resetAutoScroll();
  };

  useEffect(() => {
    if (accountId) {
      clearChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  useEffect(() => {
    const currentAbortController = abortControllerRef.current;
    return () => {
      if (currentAbortController) {
        currentAbortController.abort();
      }
    };
  }, []);

  React.useLayoutEffect(() => {
    if (atBottom || isHistoryLoading) {
      scrollToBottom(false);
    }
  }, [messages, isHistoryLoading, scrollToBottom, atBottom]);

  useEffect(() => {
    const manuallyUnpinned =
      sessionStorage.getItem("copilot_manually_unpinned") === "true";
    if (open && inputAreaRef.current) {
      inputAreaRef.current.focus();
    }
    if (open && mode === "create") {
      setIsPinned(!manuallyUnpinned);
    }
    // Pin when opened via Ask AI button, but respect session storage
    if (open && shouldPinOnOpen) {
      setIsPinned(!manuallyUnpinned);
      setShouldPinOnOpen(false);
    }
    if (!open) {
      setIsPinned(false);
    }
  }, [open, chatId, mode, shouldPinOnOpen, setShouldPinOnOpen]);

  // Update current chat ID reference when chat changes
  useEffect(() => {
    currentChatIdRef.current = chatId;
  }, [chatId]);

  const handleSend = ({
    input,
    retry,
    chatConversationId,
    messages: cleanedMessages,
  }: {
    input: string;
    retry: boolean;
    chatConversationId?: string;
    messages?: MessageType[];
  }) => {
    if (input.trim()) {
      // Set loader states first, before any async operations
      setTypingIndicatorLoading(true);
      setIsStreaming(false);

      if (retry) setChatError(null);

      // Use cleaned messages if provided (for retry), otherwise use current messages
      const baseMessages = cleanedMessages ?? messages;

      // Prepare initial messages
      let initialMessages: MessageType[] = [];
      let newMessages: MessageType[] = [];

      if (!retry) {
        // Convert attached creatives to message format
        const messageCreatives = attachedCreatives.map((c) => ({
          id: c.creative_id,
          name: c.ad_name || "Creative",
          type: c.type === "video" ? ("video" as const) : ("image" as const),
          thumbnail_url: c.thumbnail_url || "",
          media_url: c.media_url || "",
        }));

        newMessages = [
          {
            response: input,
            agent: Agent.user,
            created_at: new Date().toISOString(),
            time_taken: null,
            attachments: files,
            creatives: messageCreatives.length > 0 ? messageCreatives : undefined,
            metrics: currentSectionMetrics.length > 0 ? currentSectionMetrics : undefined,
          },
          {
            agent: Agent.assistant,
            response: "",
            reasoning: "",
            answer: "",
            chat_conversation_id: null,
            user_chat_conversation_id: null,
            assistant_chat_conversation_id: null,
            created_at: new Date().toISOString(),
            time_taken: null,
            status: "loading",
            text: "Gathering context",
          },
        ];
        // Append new messages to existing messages instead of replacing
        initialMessages = [...baseMessages, ...newMessages];
        setMessages(initialMessages);
        // Use setTimeout to ensure the DOM has updated before scrolling
        setTimeout(() => scrollToBottom(true), 0);
      } else {
        newMessages = [
          {
            agent: Agent.assistant,
            response: "",
            reasoning: "",
            answer: "",
            chat_conversation_id: null,
            user_chat_conversation_id: null,
            assistant_chat_conversation_id: null,
            created_at: new Date().toISOString(),
            time_taken: null,
            status: "loading",
            text: "Gathering context",
          },
        ];
        initialMessages = [...baseMessages, ...newMessages];
        setMessages(initialMessages);
        // Use setTimeout to ensure the DOM has updated before scrolling
        setTimeout(() => scrollToBottom(true), 0);
      }

      setInput("");
      setFiles([]);

      // Extract attachments and creatives from original message when retrying
      let retryFiles: Files[] = [];
      let retryCreativeIds: string[] = [];
      let retrySectionIds: string[] = [];
      let retryMetrics: { id: string; label: string }[] | undefined;

      if (retry) {
        // Find the last user message in cleanedMessages to extract attachments
        const lastUserMessage = baseMessages
          .slice()
          .reverse()
          .find((msg) => msg.agent === Agent.user);

        if (lastUserMessage) {
          retryFiles = lastUserMessage.attachments || [];
          retryCreativeIds = lastUserMessage.creatives?.map((c) => c.id) || [];
          retryMetrics = lastUserMessage.metrics;

          // Try to extract section IDs from the original messages state
          // (not cleanedMessages, since assistant messages may have been removed)
          // Find the user message in the original messages array
          const lastUserMessageInOriginal = messages
            .slice()
            .reverse()
            .find(
              (msg) =>
                msg.agent === Agent.user &&
                msg.response === lastUserMessage.response &&
                msg.created_at === lastUserMessage.created_at,
            );

          if (lastUserMessageInOriginal) {
            const lastUserMessageIndex = messages.findIndex(
              (msg) => msg === lastUserMessageInOriginal,
            );
            // Check the assistant message that followed this user message
            if (lastUserMessageIndex >= 0 && lastUserMessageIndex < messages.length - 1) {
              const followingAssistantMessage = messages[lastUserMessageIndex + 1];
              if (
                followingAssistantMessage?.agent === Agent.assistant &&
                followingAssistantMessage?.sections
              ) {
                retrySectionIds = followingAssistantMessage.sections.map((s) => s.id);
              }
            }
          }
        }
      }

      // Capture attached creative IDs and section ID before clearing (for non-retry)
      const creativeIds = retry
        ? retryCreativeIds
        : attachedCreatives.map((c) => c.creative_id);
      // All attached creatives are from the same section (enforced by store)
      const creativeSectionId = retry ? undefined : attachedCreatives[0]?.section_id;

      // Use files from retry message or current state
      const filesToSend = retry ? retryFiles : files;

      try {
        const basePayload = {
          question: input,
          chat_id: chatId,
          file_ids: filesToSend.map((file) => file.id),
          response_quality: llmModel,
        };

        // Use section IDs from retry message, creative's section ID, or selected source
        const sectionIds = retry
          ? retrySectionIds.length > 0
            ? retrySectionIds
            : selectedSource?.sectionIds || []
          : creativeSectionId
            ? [creativeSectionId]
            : selectedSource?.sectionIds || [];

        const reportPayload = {
          ...basePayload,
          ...(reportId && { report_id: reportId }),
          section_ids: sectionIds,
          metric_ids: retry
            ? retryMetrics?.map((m) => m.id) || selectedSource?.metricIds || []
            : selectedSource?.metricIds || [],
          ...(creativeIds.length > 0 && { creative_ids: creativeIds }),
          ...(retry &&
            chatConversationId && {
              chat_conversation_id: chatConversationId,
            }),
          ...(selectedSection?.id && { active_section_id: selectedSection.id }),
          ...creativeMetaData,
        };

        const createPayload = {
          ...basePayload,
          ...(retry &&
            chatConversationId && {
              chat_conversation_id: chatConversationId,
            }),
          ...(selectedSection?.id && { active_section_id: selectedSection.id }),
        };

        // Keep attached creatives for subsequent messages
        // clearAttachedCreatives(); // Removed to keep creatives attached after sending

        // Start background stream
        void startStream(
          mode === "create" ? createPayload : reportPayload,
          initialMessages, // Pass all current messages, not just the new ones
        );
      } catch (error) {
        console.error(error);
        setChatError("An error has occurred. Please try again.");
        setTypingIndicatorLoading(false);
        setIsStreaming(false);
      }
    }
  };

  const updateChatHistory = async () => {
    if (accountId) {
      const res = await fetchCreativeChatList(accountId);
      setChatHistory(res);
    }
  };

  const getChatHistory = async (hideLoader?: boolean) => {
    if (!hideLoader) {
      setIsHistoryLoading(true);
      setMessages([]);
    }
    try {
      if (accountId) {
        const res = await fetchCreativeChatList(accountId);
        setChatHistory(res);
      }
    } catch {
      setChatId(null);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFiles = Array.from(event.target.files);
      void handleFileUpload(selectedFiles);
    }
  };

  useEffect(() => {
    if (open) void getChatHistory(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountId]);

  const getChatHistoryById = async (id: string) => {
    if (abortControllerRef?.current) {
      abortControllerRef.current.abort();
    }
    setIsHistoryLoading(true);

    // Clean up all stream listeners to prevent cross-chat contamination
    cleanupAllListeners();
    streamListenerRef.current = null;

    // Update current chat ID reference
    currentChatIdRef.current = id;

    // Check if there's an active stream for this chat ID
    streamListenerRef.current = (stream) => {
      // Only update if this stream is for the current chat ID
      if (stream.chatId !== currentChatIdRef.current) {
        return;
      }

      // Update messages when stream updates
      setMessages(stream.messages);

      // Update loading states
      setIsStreaming(stream.status === "in_progress");
      setTypingIndicatorLoading(stream.status === "loading");

      // Handle completion and errors
      if (stream.status === "completed") {
        setTypingIndicatorLoading(false);
        setIsStreaming(false);
        setIsHistoryLoading(false);
      } else if (stream.status === "error") {
        setChatError(stream.error || "An error occurred");
        setTypingIndicatorLoading(false);
        setIsStreaming(false);
        setIsHistoryLoading(false);
      }
    };

    const existingStream = subscribeToExistingStream(id, streamListenerRef.current);

    if (existingStream) {
      // We have an active stream, but only use it if it's for the same chat ID
      if (existingStream.chatId === id) {
        setMessages(existingStream.messages);
        setIsStreaming(existingStream.status === "in_progress");
        setTypingIndicatorLoading(existingStream.status === "loading");
        setChatError(existingStream.status === "error" ? existingStream.error : null);
        setIsHistoryLoading(false);

        setChatId(id);
        updateURLChatId(id);

        console.log("Subscribed to existing stream for chat:", id);
        return;
      } else {
        console.log(
          "Active stream exists but for different chat:",
          existingStream.chatId,
          "vs requested:",
          id,
        );
        // Don't return - continue to fetch from API
      }
    }

    // No active stream, fetch from API
    try {
      let res = undefined;
      if (accountId) {
        res = await fetchCreativeChatHistory(accountId, id);
      }
      if (!res) return;

      const annotatedMessages: MessageType[] = [];
      if (res.chat_history.length > 0) {
        setChatId(id);
        updateURLChatId(id);
      } else {
        setChatId(null);
        updateURLChatId(null);
      }

      for (const msg of res.chat_history) {
        const tempMsg: MessageType = {
          ...msg,
          data_filters: undefined,
          data_point_id: msg.data_point_id || undefined,
          annotations: msg.annotations || undefined,
        };

        // Extract creatives and metrics from data_filters for both user and assistant messages
        if (msg.data_filters) {
          tempMsg.creatives = msg.data_filters.creatives;
          tempMsg.metrics = msg.data_filters?.metrics || [];
        }

        if (msg.agent === Agent.assistant && msg.data_filters) {
          tempMsg.sections = msg.data_filters?.sections;
          tempMsg.report = msg.data_filters.report;
          tempMsg.reasoning = msg.data_filters.reasonings?.join("\n");
          tempMsg.answer = msg.response;
          tempMsg.assistant_chat_conversation_id = msg.chat_conversation_id;
          annotatedMessages.push(tempMsg);
        } else {
          annotatedMessages.push(tempMsg);
        }
      }
      setMessages(annotatedMessages);

      // if last message is of user in history show error
      if (annotatedMessages.at(-1)?.agent === Agent.user) {
        setChatError("Please try again.");
      }

      setIsHistoryLoading(false);
    } catch (error) {
      console.error(error);
      setIsHistoryLoading(false);
      setMessages([]);
    }
  };

  const handleUpdateFeedback = (feedback: MessageFeedback, id: string) => {
    setMessages((prevMessages) =>
      prevMessages.map((message) => {
        if (
          message.chat_conversation_id === id ||
          message.assistant_chat_conversation_id === id
        ) {
          return {
            ...message,
            feedback: feedback,
          };
        }
        return message;
      }),
    );
  };

  // Update chat history whenever it changes
  useEffect(() => {
    onChatHistoryUpdate?.(chatHistory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatHistory]);

  // Update active chat ID whenever it changes
  useEffect(() => {
    onActiveChatIdChange?.(chatId || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  useEffect(() => {
    onMessagesChange?.(messages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  React.useImperativeHandle(ref, () => ({
    clearChat,
    getChatHistoryById,
    updateURLChatId,
    setChatError,
  }));

  const handleFileUpload = async (selectedFiles: File[]) => {
    if (selectedFiles.length > 0) {
      // Create immediate preview items with temp IDs
      const newUploadingItems = selectedFiles.map((file) => {
        const tempId = `temp-${Date.now()}-${Math.random()}`;
        const preview = URL.createObjectURL(file);
        return { file, preview, id: tempId };
      });
      // Append new uploading files to existing ones instead of replacing
      setUploadingFiles((prev) => [...prev, ...newUploadingItems]);

      try {
        const response = await uploadFilesAsync(
          selectedFiles,
          accountId || "",
          "copilot_attachment",
        );

        setFiles((prev) => {
          // Use a Map to deduplicate by id, always keeping the first occurrence
          const filesById = new Map<string, Files>();
          [...prev, ...response.files].forEach((file) => {
            if (!filesById.has(file.id)) {
              filesById.set(file.id, file);
            }
          });
          return Array.from(filesById.values());
        });
        // Remove only the completed uploading files, not all of them
        setUploadingFiles((prev) =>
          prev.filter(
            (item) => !newUploadingItems.some((newItem) => newItem.id === item.id),
          ),
        );
        // Clean up object URLs to prevent memory leaks after upload
        newUploadingItems.forEach((item) => {
          if (item.preview) {
            URL.revokeObjectURL(item.preview);
          }
        });
      } catch (error) {
        console.error("Error uploading files:", error);
        // Remove only the failed uploading files, not all of them
        setUploadingFiles((prev) =>
          prev.filter(
            (item) => !newUploadingItems.some((newItem) => newItem.id === item.id),
          ),
        );
        // Clean up object URLs on error
        newUploadingItems.forEach((item) => {
          if (item.preview) {
            URL.revokeObjectURL(item.preview);
          }
        });
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Handle local files
    const droppedFiles = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith("image/"),
    );

    if (droppedFiles.length > 0) {
      void handleFileUpload(droppedFiles);
      return;
    }

    // Handle images from web pages (like Google Images)
    const items = Array.from(e.dataTransfer.items);

    for (const item of items) {
      // Handle image data directly
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          void handleFileUpload([file]);
          return;
        }
      }

      // Handle URLs (like when dragging from Google Images)
      if (item.type === "text/uri-list" || item.type === "text/plain") {
        const url = e.dataTransfer.getData(item.type);

        if (url && (url.startsWith("http") || url.startsWith("data:image"))) {
          try {
            const response = await fetch(url);
            const blob = await response.blob();

            if (blob.type.startsWith("image/")) {
              // Create a File object from the blob
              const file = new File([blob], `image.${blob.type.split("/")[1]}`, {
                type: blob.type,
              });
              void handleFileUpload([file]);
              return;
            }
          } catch (error) {
            console.error("Error fetching image from URL:", error);
          }
        }
      }

      // Handle HTML content (sometimes contains image data)
      if (item.type === "text/html") {
        const html = e.dataTransfer.getData("text/html");

        // Extract image src from HTML
        const imgMatch = html.match(/<img[^>]+src="([^"]+)"/);
        if (imgMatch && imgMatch[1]) {
          const imgUrl = imgMatch[1];

          try {
            const response = await fetch(imgUrl);
            const blob = await response.blob();

            if (blob.type.startsWith("image/")) {
              const file = new File([blob], `image.${blob.type.split("/")[1]}`, {
                type: blob.type,
              });
              void handleFileUpload([file]);
              return;
            }
          } catch (error) {
            console.error("Error fetching image from HTML URL:", error);
          }
        }
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageFiles = items
      .filter((item) => item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);

    if (imageFiles.length > 0) {
      e.preventDefault();
      void handleFileUpload(imageFiles);
    }
  };

  const handleRemoveFile = (fileToRemove: Files) => {
    setFiles((prev) =>
      prev.filter(
        (file) =>
          (file.id && file.id !== fileToRemove.id) ||
          (!file.id && file.public_url !== fileToRemove.public_url),
      ),
    );
  };

  const isSendDisabled =
    input.trim() === "" ||
    typingIndicatorLoading ||
    isHistoryLoading ||
    isStreaming ||
    !!chatError ||
    loading;

  return (
    <SqTooltipProvider>
      <div
        style={{
          position: !isPinned ? "absolute" : "relative",
          top: !isPinned ? "0" : "auto",
          right: !isPinned ? "0" : "auto",
          backgroundColor: isFullscreen || isPinned ? "#FAFAFA" : "transparent",
          transformOrigin: "top right",
          transform: open ? "scale(1) translateX(0)" : "scale(0.85) translateX(20px)",
          opacity: open ? 1 : 0,
          transition: "all 0.6s cubic-bezier(0.34, 1.2, 0.64, 1)",
          pointerEvents: open ? "auto" : "none",
          zIndex: 30,
        }}
        className={cn("h-full py-2 px-2", !isFullscreen ? "w-[473px]" : "w-full")}
      >
        <div
          className={`flex flex-col squircle squircle-fill-[#FAFAFAF0] backdrop-blur-[4px] squircle-radius-[16px] drop-shadow-sm h-full relative before:absolute before:inset-0 before:squircle before:squircle-radius-[16px] before:squircle-outline-[0.8px] before:squircle-outline-color-[#D0D5DD] before:pointer-events-none`}
        >
          <>
            {showChatHistory && (
              <div className="flex justify-between gap-4">
                <div className="w-full">
                  <div className={`flex justify-between border-b p-2`}>
                    <div className={`flex gap-3 items-center w-full justify-between`}>
                      <div className="flex items-center gap-1">
                        <SqButton
                          variant={"fill"}
                          bgColor="#FF8F66"
                          size={"sm"}
                          shadow={"sm"}
                          onClick={() => clearChat()}
                          className={`${
                            messages.length > 0 ? "cursor-pointer" : "!cursor-not-allowed"
                          }`}
                        >
                          <RiEditLine size={14} color="#FFFFFF" className="shrink-0" />
                          <span className="font-medium text-[11px] leading-[19.16px] tracking-[0.02em]">
                            New Chat
                          </span>
                        </SqButton>
                        <div className="cursor-pointer flex items-center justify-center">
                          <InsightChatHistoryMenu
                            data={chatHistory}
                            activeChatId={chatId || ""}
                            onChatSelect={(item) => {
                              updateURLChatId(item.id);
                              setChatId(item.id);
                              // Clear any existing stream states when switching chats
                              setIsStreaming(false);
                              setTypingIndicatorLoading(false);
                              setChatError(null);
                              resetAutoScroll();
                              if (item.extra) {
                                setLlmModel(item.extra.response_quality);
                              }
                              void getChatHistoryById(item.id);
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {!isFullscreen && (
                          <SqTooltip>
                            <SqTooltipTrigger asChild>
                              <SqButton
                                variant={"icon"}
                                shadow={"sm"}
                                hasOutline
                                bgColor={isPinned ? "#9AA3BD" : "white"}
                                onClick={() => {
                                  const newPinnedState = !isPinned;
                                  setIsPinned(newPinnedState);
                                  // Update session storage when user manually pins/unpins
                                  if (newPinnedState) {
                                    sessionStorage.removeItem(
                                      "copilot_manually_unpinned",
                                    );
                                  } else {
                                    sessionStorage.setItem(
                                      "copilot_manually_unpinned",
                                      "true",
                                    );
                                  }
                                }}
                                hoverBgColor={isPinned ? "#9AA3BD" : "#fbfbfb"}
                                size={"sm"}
                                className="cursor-pointer"
                              >
                                {isPinned ? (
                                  <RiPushpinFill
                                    size={14}
                                    color="white"
                                    className="shrink-0"
                                  />
                                ) : (
                                  <RiPushpinLine
                                    size={14}
                                    color="#9AA3BD"
                                    className="shrink-0"
                                  />
                                )}
                              </SqButton>
                            </SqTooltipTrigger>
                            <SqTooltipContent>
                              {isPinned ? "Unpin" : "Pin"}
                            </SqTooltipContent>
                          </SqTooltip>
                        )}
                        <SqTooltip>
                          <SqTooltipTrigger asChild>
                            <SqButton
                              variant={"icon"}
                              hasOutline
                              className="h-7 w-7"
                              shadow={"sm"}
                              bgColor="white"
                              onClick={() => {
                                setIsPinned(false);
                                setIsFullscreen((prev) => !prev);
                              }}
                              hoverBgColor="#fbfbfb"
                            >
                              {isFullscreen ? (
                                <RiContractLeftRightLine
                                  size={14}
                                  color="#9AA3BD"
                                  className="shrink-0"
                                  style={{ transform: "rotate(-45deg)" }}
                                />
                              ) : (
                                <RiExpandLeftRightLine
                                  size={14}
                                  color="#9AA3BD"
                                  className="shrink-0"
                                  style={{ transform: "rotate(-45deg)" }}
                                />
                              )}
                            </SqButton>
                          </SqTooltipTrigger>
                          <SqTooltipContent>
                            {isFullscreen ? "Collapse" : "Expand"}
                          </SqTooltipContent>
                        </SqTooltip>

                        {onChatOpenChange && (
                          <SqButton
                            variant="outline"
                            onClick={() => {
                              onChatOpenChange(!open);
                            }}
                            className="px-[8px] py-[4px] pr-[12px]"
                            size={"sm"}
                            shadow={"sm"}
                          >
                            <div className="flex items-center gap-[6px]">
                              <RiCloseFill
                                size={14}
                                color="#9AA3BD"
                                className="shrink-0"
                              />
                              <p className="flex items-end text-brand-primary-new/80 text-[11px] font-medium tracking-[0.24px] leading-none translate-y-[1px]">
                                Close
                              </p>
                            </div>
                          </SqButton>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div
              className={`relative flex-1 overflow-y-auto overflow-x-hidden flex flex-col justify-end`}
            >
              {isHistoryLoading ? (
                <div className="flex flex-col gap-4 items-center justify-center w-full h-full">
                  <ItemLoader />
                </div>
              ) : (
                <div
                  className={cn(
                    "h-full pb-0 w-full px-0 flex flex-col items-center",
                    styles.scrollbarmodifier,
                  )}
                  ref={messageContainerRef}
                >
                  {messages?.length > 0 ? (
                    <>
                      {messages.map((msg, index) => (
                        <MessageCard
                          key={index}
                          selectedAccount={selectedAccount}
                          chatId={chatId || ""}
                          msg={msg}
                          index={index}
                          totalMessages={messages.length}
                          handleUpdateFeedback={handleUpdateFeedback}
                          showIcon={reportConfig?.type !== ReportType.Dashboard}
                        />
                      ))}
                      {!chatError && !atBottom && messages.length > 1 && (
                        <div className="absolute bottom-0 left-[1%] flex items-end justify-center bg-gradient-to-t from-[#f9f9f9] to-transparent h-24 w-[98%]">
                          <div
                            className="mb-[18px] p-2 h-[36px] w-[36px] flex items-center justify-center bg-white rounded-[8px] text-title-primary cursor-pointer shadow-lg"
                            onClick={() => scrollToBottom(true)}
                          >
                            <LuArrowDown
                              size={19}
                              className="text-brand-primary-800 shrink-0"
                            />
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {messages.length === 0 && (
                        <div className="w-full max-w-2xl flex items-center justify-center mt-6 px-4 mb-[100px]">
                          <InsightChatCarousel
                            onCardClick={(question) =>
                              void handleSend({ input: question, retry: false })
                            }
                            slides={slides}
                            disableClick={loading}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className={`relative w-full max-w-[748px] mx-auto pb-[10px] px-[10px]`}>
              {chatError && (
                <div className="px-6 py-3 flex w-full justify-between items-center gap-2 border-x">
                  <span className="text-xs font-medium leading-[18px] text-left text-red">
                    {chatError || "An error has occurred. Please try again."}
                  </span>
                  <Button
                    className="gap-1 rounded-full"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      const updatedMessages = [...messages];
                      const chatConversationId =
                        updatedMessages.at(-1)?.user_chat_conversation_id;

                      while (
                        updatedMessages.length > 0 &&
                        updatedMessages.at(-1)?.agent !== Agent.user
                      ) {
                        updatedMessages.pop();
                      }
                      const question = updatedMessages.at(-1)?.response;

                      if (question) {
                        void handleSend({
                          input: question,
                          retry: true,
                          chatConversationId: chatConversationId || undefined,
                          messages: updatedMessages,
                        });
                      }

                      e.preventDefault();
                    }}
                  >
                    <RxReload /> Retry
                  </Button>
                </div>
              )}
              <div
                className={`border rounded-[16px] bg-white px-4 py-2 relative`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={(e) => {
                  void handleDrop(e);
                }}
              >
                {[...uploadingFiles, ...files].length > 0 && (
                  <FileAttachmentsPreview
                    files={files}
                    onRemove={handleRemoveFile}
                    uploadingFiles={uploadingFiles}
                  />
                )}
                {attachedCreatives.length > 0 && (
                  <div className="w-full overflow-x-auto flex gap-2 mb-2 py-2 scrollbar-hide">
                    {attachedCreatives.map((creative) => (
                      <CreativeAttachmentCard
                        key={creative.creative_id}
                        name={creative.ad_name || "Creative"}
                        thumbnailUrl={creative.thumbnail_url}
                        mediaUrl={creative.media_url}
                        type={creative.type}
                        metrics={currentSectionMetrics}
                        showRemoveButton={true}
                        onRemove={() =>
                          removeAttachedCreative(
                            creative.creative_id,
                            creative.section_id,
                          )
                        }
                      />
                    ))}
                  </div>
                )}
                <TextareaAutosize
                  ref={inputAreaRef}
                  minRows={1}
                  maxRows={4}
                  value={input}
                  disabled={!!chatError}
                  onChange={(e) => setInput(e.target.value)}
                  onPaste={handlePaste}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      if (
                        typingIndicatorLoading ||
                        isHistoryLoading ||
                        isStreaming ||
                        loading
                      ) {
                        // Insert a new line like Shift+Enter
                        e.preventDefault();
                        setInput(input + "\n");
                      } else if (!chatError) {
                        // If none of the conditions are true, submit the message
                        void handleSend({ input: input, retry: false });
                        e.preventDefault();
                      }
                    }
                  }}
                  className="resize-none w-full p-2 pl-0 pt-1 focus:outline-none disabled:bg-[#fff] text-[14px] text-brand-primary-new placeholder:text-brand-primary-new40"
                  placeholder="The more details, the better..."
                />
                <div className={`flex items-start justify-between gap-2 my-1`}>
                  <div className="flex items-center gap-2">
                    <FileAttachmentHandler
                      acceptableFileTypes={COPILOT_ALLOWED_FILE_TYPES.join(",")}
                      isFileUploading={false}
                      fileInputRef={fileInputRef}
                      onFileChange={(e) => void handleFileChange(e)}
                      className="h-8 w-8"
                    />
                    <ModelSelectorDropdown
                      selectedModelId={llmModel}
                      onModelChange={(modelId) => {
                        setLlmModel(modelId);
                      }}
                    />

                    {showSectionDropdown &&
                      reportConfig &&
                      reportConfig.sections &&
                      reportConfig.sections.length > 0 &&
                      attachedCreatives.length === 0 && (
                        <SectionSelectorDropdown
                          sections={reportConfig.sections.map((section) => ({
                            id: section.id,
                            title: section.title,
                            icon: section.icon,
                            sectionIds: [section.id],
                          }))}
                          selectedSource={selectedSource}
                          onSourceChange={(source) => {
                            if (source.id === selectedSource?.id) {
                              return;
                            }
                            setSelectedSource(source);
                          }}
                          showIcon={reportConfig.type !== ReportType.Dashboard}
                        />
                      )}
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <SqButton
                      onClick={() => void handleSend({ input: input, retry: false })}
                      variant="fill"
                      className={`shrink-0 w-[32px] h-[32px] rounded-[8px]
                            ${
                              isSendDisabled
                                ? "!squircle-fill-[#F6F6F6] text-[#9aa3bd] cursor-not-allowed" // Custom disabled state color
                                : "!squircle-fill-[#FF4500] hover:!squircle-fill-[#F42100] text-white hover:text-white"
                            } 
                            p-0`}
                      disabled={isSendDisabled}
                    >
                      <RiSendPlane2Line size={14} className="shrink-0" />
                    </SqButton>
                  </div>
                </div>
              </div>
            </div>
          </>
        </div>
      </div>
    </SqTooltipProvider>
  );
});
